import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Text } from '@kula-ai/components';
import type { EmailApiHandle } from '@kula-ai/components';
import { useRestoreStolenFocus } from './useRestoreStolenFocus';
import { CenterSpinner } from '@/components/ats/loadders';
import { FormBuilder } from '@/components/formBuilder';
import { useAuth, useShowToast } from '@/hooks';
import { useMixpanel } from '@/hooks/use-mixpanel';
import { getErrorMessage } from '@/utils/errors';
import { templatesCategoryConfig } from '@/constants/templates-category';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getInvalidVariableAlertConfig } from '@/formConfigs/getInvalidVariableAlertConfig';
import { findBlockingVariableProblem } from '@/utils/liquidSyntax';
import {
  toVariablesList,
  validateEmailComposition,
} from '@/utils/emailComposition';
import {
  useInternalEmail,
  useInternalEmailPrefill,
  useSendInternalEmail,
  useStartInternalEmailScorecardsPdf,
  useUpdateInternalEmail,
} from '@/api/internalEmails';
import { SendInternalEmailComposer } from './SendInternalEmailComposer';
import { ScheduleForLater } from '../assessment/send-assessment/schedule/ScheduleForLater';
import { AttachDocumentsControl } from './AttachDocumentsControl';
import { SelectedAttachmentChips } from './SelectedAttachmentChips';
import {
  applyTemplateAttachments,
  applyTemplateDocumentKinds,
  removeAttachment,
  toAttachmentChips,
  toEditSelection,
  toSendSelection,
} from './attachDocumentSelection';
import {
  toEditInternalEmailValues,
  toReplyInternalEmailValues,
  toSendInternalEmailPayload,
  toSendInternalEmailValues,
} from './sendInternalEmailValue';
import type { AttachmentSelection } from './attachDocumentSelection';
import type { InternalEmailTemplate } from './internalEmailTemplates';
import type { SendInternalEmailValues } from './sendInternalEmailValue';

interface SendInternalEmailDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  activity?: { id?: string | number };
  application?: { id?: string | number; job_id?: string | number };
  /** Set to edit a queued email instead of composing a new one. */
  internalEmailId?: string | number;
  /**
   * Set to reply to an internal email already sent. The composer opens addressed
   * to everyone on that thread, and the send threads under it.
   */
  replyToInternalEmailId?: string | number;
}

const DRAWER_TITLE = 'Send internal email';
// Half the app's 6s default. These say a send is under way rather than asking
// for anything, and the composer they replace has just closed — 6s left them
// sitting over the list the sender went back to (CORE-5384).
const TOAST_MS = 3000;

/** The banner `getInvalidVariableAlertConfig` renders, which every surface scrolls
 *  to by this id rather than repeating what it says. */
const VARIABLE_ALERT_SELECTOR = '#invalid-variables-alert';

const SEND_FAILED = 'Sending the internal email failed';
const SEND_LABEL = 'Send internal email';
const SAVE_LABEL = 'Save';
const EDIT_TITLE = 'Edit internal email';
const SAVE_FAILED = 'Saving the internal email failed';
const SAVE_SUCCEEDED = 'Internal email updated';
const REPLY_TITLE = 'Reply to internal email';
const REPLY_LABEL = 'Send reply';

/**
 * Stretches the form down the drawer so the editor can fill what is left.
 *
 * FormBuilder's Box, its form and its SimpleGrid all shrink-wrap, so without
 * this the editor sat at kula's fixed 320px default and the drawer showed a
 * band of empty space above the footer. Targeted by depth — none of the three
 * has a class of its own.
 *
 * `flex: 1` and nothing else: each level grows into spare room, and a column
 * flex item's default `min-height: auto` keeps it from shrinking below its
 * content. Pinning `min-height: 0` did the opposite — a composition taller than
 * the drawer (a row of role chips and a dozen recipients is enough) was squeezed
 * until kula's card, which clips, cut ~200px off the bottom. Nothing overflowed
 * the drawer's own scroller, so there was nothing to scroll to: the subject and
 * the body were simply unreachable.
 *
 * The bottom padding is the breathing room between the body and the footer, on
 * top of the drawer body's own 16px gutter — together about the 50px asked for.
 */
const FORM_CONTAINER_PROPS = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  pb: '32px',
  sx: {
    '& > form': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
    '& > form > div': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
  },
} as const;

/** What the shared schedule picker hands back on confirm. */
interface ScheduleFormValues {
  schedule_at?: {
    formattedTime?: string;
    timezoneValue?: { key?: string };
  };
}

/**
 * Sends an internal email about a candidate to teammates.
 *
 * Everything is pre-filled from the activity — sender, recipients (people and
 * roles) and the email body — and can be edited before sending. Documents are
 * attached by reference from the candidate's existing files; only a new upload
 * carries bytes from this device.
 */
export const SendInternalEmailDrawer = ({
  isOpen = true,
  onClose = () => {},
  onSuccess = () => {},
  activity = {},
  application = {},
  internalEmailId,
  replyToInternalEmailId,
}: SendInternalEmailDrawerProps) => {
  // Radix's focus trap steals the first click out of the recipients field.
  useRestoreStolenFocus(isOpen);
  const isEditing = Boolean(internalEmailId);
  const isReplying = Boolean(replyToInternalEmailId);
  const formRef = useRef<{ submitForm?: () => void } | null>(null);
  // kula's handle on the composer, for scrolling a refused send into view.
  const emailApiRef = useRef<EmailApiHandle>(null);
  const toast = useShowToast();
  const { currUser } = useAuth();
  const { track } = useMixpanel();
  // `null` until seeded, so an edit can tell "not loaded yet" from "nothing attached"
  // — the email arrives after the first render, and defaulting to the empty selection
  // would let a save go out having silently dropped every file on it.
  const [picked, setPicked] = useState<AttachmentSelection | null>(null);
  // Set immediately before the submit that carries it — the send time is not a
  // form field, so nothing else writes it.
  const sendTimeRef = useRef<{ at: string | null; timezone: string | null }>({
    at: null,
    timezone: null,
  });
  const scheduleFormRef = useRef(null);

  const { data: prefill, isLoading: isPrefillLoading } =
    useInternalEmailPrefill({
      applicationId: application?.id,
      activityId: activity?.id,
      enabled: isOpen,
    });

  // Editing reads the queued email itself; the activity's configuration is what
  // the email was built FROM, not what it currently says.
  const { data: editing, isLoading: isEditingLoading } = useInternalEmail(
    internalEmailId,
    { enabled: isOpen && isEditing }
  );
  // A reply reads the email it answers for its recipients and subject, and still
  // needs the ad-hoc prefill for the candidate's documents.
  const { data: repliedTo, isLoading: isRepliedToLoading } = useInternalEmail(
    replyToInternalEmailId,
    { enabled: isOpen && isReplying }
  );
  const isLoading = isEditing
    ? isEditingLoading
    : isPrefillLoading || (isReplying && isRepliedToLoading);

  const view = templatesCategoryConfig.internal_email_notice.view;
  const { data: { variables = {} } = {} } = useGetTemplatesVariablesQuery({
    params: { view },
  });
  const variablesListArr = toVariablesList(variables);

  const { mutateAsync: sendInternalEmail, isPending: isSending } =
    useSendInternalEmail();
  const { mutateAsync: updateInternalEmail, isPending: isSaving } =
    useUpdateInternalEmail();
  const isPending = isSending || isSaving;

  const initialValues = useMemo(() => {
    if (isEditing) return toEditInternalEmailValues(editing, currUser);
    if (isReplying)
      return toReplyInternalEmailValues(repliedTo, prefill, currUser);
    return toSendInternalEmailValues(prefill, currUser);
  }, [isEditing, isReplying, editing, repliedTo, prefill, currUser]);

  /**
   * What the picker opens with ticked.
   *
   * An edit reads the queued email itself: what it carries is what the sender
   * chose, and the activity's configuration is only what it was built from.
   *
   * A reply composes its own message — the activity's template is not applied to
   * it, so neither its files nor its document kinds belong on it.
   *
   * A fresh send starts from the activity: the kinds it configured, resolved
   * against this candidate's own documents. The same helper a template pick
   * uses, because it is the same act — the configuration says what to tick, and
   * the sender is free to tick anything else.
   */
  const seeded = useMemo(() => {
    if (isEditing) return toEditSelection(editing, prefill?.documents);
    if (isReplying) return toSendSelection(undefined);

    return applyTemplateDocumentKinds(
      toSendSelection(prefill),
      prefill?.attach_document_kinds,
      prefill?.documents
    );
  }, [isEditing, isReplying, editing, prefill]);
  const attachments = picked ?? seeded;

  const chips = useMemo(
    () => toAttachmentChips(attachments, prefill?.documents),
    [attachments, prefill?.documents]
  );

  const handleRemoveAttachment = useCallback(
    (key: string) =>
      setPicked((current) => removeAttachment(current ?? seeded, key)),
    [seeded]
  );

  const { mutate: startScorecardsPdf } = useStartInternalEmailScorecardsPdf();

  // Picking a template applies what it says to attach at the same moment it applies
  // the subject and body: its document kinds, resolved against this candidate's own
  // documents, and the files the template itself carries.
  const handleTemplateApplied = (template: InternalEmailTemplate | null) => {
    setPicked((current) => {
      const base = current ?? seeded;
      const withFiles = template?.attachments?.length
        ? applyTemplateAttachments(base, template)
        : base;
      return applyTemplateDocumentKinds(
        withFiles,
        template?.attach_document_kinds ?? [],
        prefill?.documents
      );
    });
  };

  // Ticking the scorecards row is what asks for the export — it is rendered on
  // demand, not one of the candidate's files. Fired on the transition rather than on
  // every change, though the server gate makes a repeat harmless.
  const handleAttachmentsChange = (selection: AttachmentSelection) => {
    const newlyPicked =
      selection.includeScorecards && !attachments.includeScorecards;
    if (newlyPicked && application?.id) {
      startScorecardsPdf({
        applicationId: application.id,
        activityId: activity?.id,
      });
    }
    setPicked(selection);
  };

  const submitWith = (at: string | null, timezone: string | null) => {
    sendTimeRef.current = { at, timezone };
    formRef.current?.submitForm?.();
  };

  // Saving an edit keeps the time it was already queued for; changing that time
  // is what "Schedule for later" is for. Sending it right now is the row's own
  // "Send now", not a button hidden inside the editor.
  const handlePrimary = () =>
    isEditing
      ? submitWith(editing?.scheduled_at ?? null, editing?.timezone ?? null)
      : submitWith(null, null);

  const handleScheduleSubmit = (values: ScheduleFormValues) => {
    const schedule = values?.schedule_at;
    if (!schedule?.formattedTime) return;
    submitWith(schedule.formattedTime, schedule.timezoneValue?.key ?? null);
  };

  const handleFormSubmit = async (values: SendInternalEmailValues) => {
    /**
     * The composer marks the offending field, but the drawer scrolls — the
     * form is taller than the panel, so on a long message the reason a send
     * was refused sat below the fold and the press read as nothing happening
     * (CORE-5404). kula picks the error to go to: from → to → subject →
     * content, on the next frame, so this can be called in the same handler
     * that refuses.
     */
    const showWhyItStopped = () => emailApiRef.current?.focusFirstError();

    // Blocking the send is ours; the composer already shows the inline error
    // against the offending field once `submitCount` bumps, so no toast — a
    // recipient that is missing says so on the field the reader is being sent
    // to. An emptied editor serialises as `<p></p>`, which no truthiness check
    // catches.
    if (!values?.to?.length) {
      showWhyItStopped();
      return;
    }
    if (
      validateEmailComposition({
        from: values?.from,
        subject: values?.subject,
        body: values?.body,
      })
    ) {
      showWhyItStopped();
      return;
    }

    /**
     * Content Rails will refuse. Blocked here rather than round-tripped, which is
     * what CORE-5404 is: the send went anyway, came back 422, and the sender was
     * handed a toast reading "Something went wrong. Please try again later" on top
     * of a banner that already said the subject was missing its closing `}}`.
     * `internal-email.js` maps neither `err_invalid_syntax` nor `err_empty_variable`,
     * so the toast could only ever be the generic one.
     *
     * The banner is the message — scrolled to, not repeated, and no toast, for the
     * same reason the two guards above have none. It sits above a composer taller
     * than the panel, so on a long body it was below the fold and the press read as
     * nothing happening.
     *
     * Deliberately narrower than the banner's OTHER half: an unknown variable NAME
     * stays a warning, because the list it is checked against is not the product's
     * variable surface (the same over-reach that had to be reverted server-side in
     * CORE-5463). Only what the server actually refuses blocks the button.
     */
    if (
      findBlockingVariableProblem({
        subject: values?.subject,
        body: values?.body,
      })
    ) {
      document
        .querySelector(VARIABLE_ALERT_SELECTOR)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const payload = toSendInternalEmailPayload({
      values,
      applicationId: application?.id as number | string,
      activityId: activity?.id,
      attachments,
      scheduleAt: sendTimeRef.current.at,
      timezone: sendTimeRef.current.timezone ?? currUser?.time_zone,
      replyToInternalEmailId,
    });

    try {
      if (isEditing) {
        await updateInternalEmail({
          id: internalEmailId as number | string,
          payload,
        });
        toast({ title: SAVE_SUCCEEDED, status: 'success', duration: TOAST_MS });
      } else {
        const sent = await sendInternalEmail(payload);
        /**
         * The counts the cap is actually checked against, which is why they come off
         * the response and not off `values`: a chip there may be one role covering
         * forty people, so chip counts cannot answer whether anyone is near the
         * limit. `to` is one entry per copy sent, `cc`/`bcc` the deduped recipients.
         *
         * Only a real send — an edit of a queued email goes down the branch above.
         */
        track({
          event: 'internal-email-sent',
          prop: {
            to_count: sent?.to?.length ?? 0,
            cc_count: sent?.cc?.length ?? 0,
            bcc_count: sent?.bcc?.length ?? 0,
            application_id: application?.id,
            is_reply: Boolean(replyToInternalEmailId),
            is_scheduled: Boolean(sendTimeRef.current.at),
          },
        });
        toast({
          title: sendTimeRef.current.at
            ? 'Internal email scheduled successfully'
            : 'Internal email sending in progress',
          status: sendTimeRef.current.at ? 'success' : 'info',
          duration: TOAST_MS,
        });
      }
      onClose();
      onSuccess();
    } catch (error) {
      toast({
        title: isEditing ? SAVE_FAILED : SEND_FAILED,
        status: 'error',
        // The thrown Error's message is the raw `err_…` code — `responseData`
        // carries the body it came from, which is what the error map reads. The
        // sender used to be shown the code itself and had no way to act on it.
        description: getErrorMessage(
          (error as { responseData?: unknown })?.responseData ?? {
            errors: [(error as Error)?.message],
          },
          'internalEmail'
        ),
      });
    }
  };

  const formConfig = [
    {
      id: 'internalEmailComposer',
      type: 'custom',
      Field: SendInternalEmailComposer,
      props: {
        variables,
        jobId: application?.job_id,
        onTemplateApplied: handleTemplateApplied,
        // Served rather than carried, so the picker greys rows out against the
        // same numbers the send is refused on.
        recipientLimits: prefill?.recipient_limits,
        // In the editor toolbar, not a button under it: attaching is part of
        // composing, and the toolbar is where the design puts it. The trailing
        // cluster, so it lands beside the variable picker rather than out among
        // the formatting controls.
        apiRef: emailApiRef,
        toolbarEndSlot: (
          <AttachDocumentsControl
            documents={prefill?.documents}
            value={attachments}
            onChange={handleAttachmentsChange}
          />
        ),
        footerAddon: (
          <SelectedAttachmentChips
            chips={chips}
            onRemove={handleRemoveAttachment}
          />
        ),
      },
    },
    getInvalidVariableAlertConfig({ variablesListArr }),
  ];

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      // kula's header hardcodes `text-base`, which its own CSS redefines to 14px.
      title={
        <Text variant="h2">
          {isEditing ? EDIT_TITLE : isReplying ? REPLY_TITLE : DRAWER_TITLE}
        </Text>
      }
      // Matches the candidate email drawer: 700px, overlay at 1400. kula's `size`
      // is a viewport share and its overlay sits at 1300, so both need forcing.
      size="md"
      className="!w-[700px] !max-w-[700px] data-[state=open]:animate-drawer-in-right data-[state=closed]:animate-drawer-out-right"
      overlayClassName="!z-[1400] data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out"
      // A column, so the form below can claim the height the editor fills.
      bodyClassName="flex flex-col"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            data-test-id="secondary-cta-button"
          >
            Cancel
          </Button>
          {/* Not on a reply. It answers a thread someone is already reading,
              so it goes now — queueing it for next week would arrive against a
              conversation that has moved on, and the row it lands in has no
              pending state to show for it. The other two keep it: a fresh send
              can be timed, and an edit exists to change the time it was
              queued for. */}
          {!isReplying && (
            <ScheduleForLater
              scheduleRef={scheduleFormRef}
              onSchedule={handleScheduleSubmit}
              isScheduling={isPending}
            />
          )}
          <Button
            variant="primary"
            onClick={handlePrimary}
            isLoading={isPending}
            data-test-id="primary-cta-button"
          >
            {isEditing ? SAVE_LABEL : isReplying ? REPLY_LABEL : SEND_LABEL}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <CenterSpinner />
      ) : (
        <FormBuilder
          initialValues={initialValues}
          config={formConfig}
          onSubmit={handleFormSubmit}
          formRef={formRef}
          formContainerProps={FORM_CONTAINER_PROPS}
        />
      )}
    </Drawer>
  );
};
